/**
 * 月次ふりかえりの指標（§10.1）と来月のSPF推奨ロジック（§10.2）。
 */
import { DIRECT_SUN_SED_PER_H, MED_SED, S_WARN_RATIO } from "./constants.js";
import type { DailyAggregate, UserSettings } from "./types.js";

export interface MonthlySummary {
  /** 浴びた量 ΣS_act [SED] */
  sedAct: number;
  /** 防げた量 Σ(S_bare − S_act) [SED] */
  sedPrevented: number;
  /** カット率 1 − ΣS_act/ΣS_bare */
  cutRate: number;
  /** 直射換算 [h]（UVI8相当の真夏快晴正午） */
  directSunHours: number;
  /** アラート通りならさらに防げた量 Σ(S_act − S_adh) [SED] */
  sedAdherenceGain: number;
  /** 塗り忘れ日数（屋外30分以上かつ塗布0回） */
  forgottenDays: number;
  daysCounted: number;
}

export function summarizeMonth(days: DailyAggregate[]): MonthlySummary {
  const sedAct = days.reduce((s, d) => s + d.sedAct, 0);
  const sedBare = days.reduce((s, d) => s + d.sedBare, 0);
  const sedAdh = days.reduce((s, d) => s + d.sedAdh, 0);
  return {
    sedAct,
    sedPrevented: sedBare - sedAct,
    cutRate: sedBare > 0 ? 1 - sedAct / sedBare : 0,
    directSunHours: (sedBare - sedAct) / DIRECT_SUN_SED_PER_H,
    sedAdherenceGain: Math.max(0, sedAct - sedAdh),
    forgottenDays: days.filter((d) => d.minOutdoor >= 30 && d.applications === 0).length,
    daysCounted: days.length,
  };
}

export const SPF_CANDIDATES = [20, 30, 50, 51] as const; // 51 = 「50+」表示

/**
 * 来月のSPF推奨（§10.2）：
 * SPF候補を昇順に試し、S_adh(SPF)の日次最大が 0.7×MED 以下となる日が95%以上になる最小SPF。
 * s_adh(spf) の近似：当月の日次 sedBare に対し、遵守時の防御を SPF^1.0（r=1.0）の
 * 平均残存 0.75 で掛けた値を使う【近似・要校正】。
 */
export function recommendSpf(
  days: DailyAggregate[],
  settings: UserSettings,
): { spf: number; label: string } {
  const med = MED_SED[settings.skinType];
  const threshold = S_WARN_RATIO * med;
  const MEAN_RETENTION = 0.75;
  for (const spf of SPF_CANDIDATES) {
    const pMean = 1 + (spf - 1) * MEAN_RETENTION;
    const okDays = days.filter((d) => d.sedBare / pMean <= threshold).length;
    if (days.length === 0 || okDays / days.length >= 0.95) {
      return { spf, label: spf === 51 ? "SPF50+" : `SPF${spf}` };
    }
  }
  return { spf: 51, label: "SPF50+" };
}
